<template>
  <div
    class="bg-site"
    :class="{
      'menu-open': menuOpen,
      'bg-site--ready': pageReady,
      'bg-site--booting': pageBooting,
      'bg-site--maintenance': siteMaintenanceActive
    }"
  >
    <transition name="bg-page-loader">
      <BlackGoldPageLoader v-if="pageBooting" />
    </transition>

    <BlackGoldMaintenanceOverlay
      v-if="siteMaintenanceActive"
      :message="siteMaintenanceMessage"
    />

    <transition name="bg-page-loader">
      <BlackGoldPageLoader
        v-if="gameLaunchLoading"
        :text="$t('games.loading')"
        overlay-class="bg-game-launch-loader"
      />
    </transition>

    <button
      v-if="menuOpen"
      type="button"
      class="mobile-menu-backdrop"
      :aria-label="$t('nav.menuClose')"
      @click="closeMobileMenu"
    />

    <div class="app-shell">
      <BlackGoldHeader
        :active-nav="activeNav"
        :top-nav-items="mainNavItems"
        :is-logged-in="loggedIn"
        :login-id="member.loginid"
        :balance="member.balance"
        :balance-loading="balanceLoading"
        :menu-open="menuOpen"
        :slip-unread-count="slipUnreadCount"
        @home="goHome"
        @nav="onTopNav"
        @login="onLogin"
        @signup="onSignup"
        @lang="onLang"
        @refresh-balance="loadMemberRuntime"
        @logout="onLogout"
        @open-slip="openSlipPage"
        @toggle-menu="toggleMobileMenu"
      />

      <div class="app-layout">
        <BlackGoldSidebar
          v-if="pageReady"
          :active-side="activeSide"
          :telegram-id="telegramId"
          :side-menu="sideMenuItems"
          :menu-badges="sideMenuBadges"
          @nav="onSideNav"
          @home="goHome"
          @telegram="onTelegram"
        />

        <main class="main-content" :class="{ 'main-content--sports': mainView === 'sports' }">
          <template v-if="mainView === 'home'">
            <template v-if="pageReady">
              <BlackGoldHero />
              <BlackGoldGames
                :hot-games="hotGames"
                :loading="hotGamesLoading"
                :load-error="hotGamesError"
                @preview="openSlotPreview"
              />
              <BlackGoldProviders
                :providers="popularProviders"
                :loading="providersLoading"
                :load-error="providersError"
                :title="$t('games.hotProvidersTitle')"
                :subtitle="$t('games.hotProvidersSubtitle')"
                @select="onSelectProvider"
              />
            </template>
          </template>
          <BlackGoldProviders
            v-else-if="mainView === 'live'"
            section-id="section-live"
            :title="$t('games.liveTitle')"
            :subtitle="$t('games.liveSubtitle')"
            layout="grid"
            :show-header-actions="false"
            :providers="liveProviders"
            :loading="providersLoading"
            :load-error="providersError"
            @select="onSelectLiveProvider"
          />
          <BlackGoldSlotGames
            v-else-if="mainView === 'slots' && selectedSlotProvider"
            :provider-name="selectedSlotProvider.name"
            :games="slotGames"
            :loading="slotGamesLoading"
            :load-error="slotGamesError"
            @back="clearSlotProvider"
            @preview="openSlotPreview"
          />
          <BlackGoldProviders
            v-else-if="mainView === 'slots'"
            section-id="section-slots"
            :title="$t('games.slotsTitle')"
            :subtitle="$t('games.slotsSubtitle')"
            layout="grid"
            :show-header-actions="false"
            :providers="slotProviders"
            :loading="providersLoading"
            :load-error="providersError"
            @select="onSelectSlotProvider"
          />
          <BlackGoldSportsFrame
            v-else-if="mainView === 'sports'"
            :frame-url="sportsFrameUrl"
            :loading="sportsFrameLoading"
            :error="sportsFrameError"
          />
          <BlackGoldSlotGames
            v-else-if="mainView === 'minigames'"
            section-id="section-minigames"
            :title="$t('games.minigameTitle')"
            :show-back="false"
            :provider-name="minigameProviderName"
            :games="minigameGames"
            :loading="minigameGamesLoading"
            :load-error="minigameGamesError"
            @preview="openMinigamePreview"
          />
          <BlackGoldSlotGames
            v-else-if="mainView === 'fishing'"
            section-id="section-fishing"
            :title="$t('games.fishingTitle')"
            :show-back="false"
            :provider-name="$t('games.fishingTitle')"
            :games="fishingGames"
            :loading="fishingGamesLoading"
            :load-error="fishingGamesError"
            @preview="openFishingPreview"
          />
          <BlackGoldMyPage
            v-else-if="mainView === 'mypage'"
            ref="myPage"
            :wallet-enabled="walletEnabled"
            @deposit="onDeposit"
            @withdraw="onWithdraw"
            @balance-updated="onMyPageBalanceUpdated"
            @inquiry="onInquiry"
            @betting="onBettingHistory"
          />
          <BlackGoldNoticePage
            v-else-if="mainView === 'notice'"
            ref="noticePage"
            @open-detail="openNoticeDetail"
          />
          <BlackGoldEventPage
            v-else-if="mainView === 'event'"
            ref="eventPage"
            @open-detail="openNoticeDetail"
          />
          <BlackGoldSupportPage
            v-else-if="mainView === 'support'"
            ref="supportPage"
            @open-detail="openQuestionDetail"
          />
          <BlackGoldSlipPage
            v-else-if="mainView === 'slip'"
            ref="slipPage"
            :unread-count="slipUnreadCount"
            @open-detail="openSlipDetail"
          />
          <BlackGoldBettingPage
            v-else-if="mainView === 'betting'"
            ref="bettingPage"
          />
          <BlackGoldDepositPage
            v-else-if="mainView === 'deposit'"
            ref="depositPage"
            @telegram="onTelegram"
            @support="openSupportPage"
            @success="onDepositSuccess"
          />
          <BlackGoldWithdrawPage
            v-else-if="mainView === 'withdraw'"
            ref="withdrawPage"
            @telegram="onTelegram"
            @support="openSupportPage"
            @success="onWithdrawSuccess"
          />
        </main>
      </div>

      <BlackGoldMobileNav
        :active-key="mobileNavActive"
        :nav-items="mobileNavItems"
        @nav="onMobileNav"
      />
    </div>

    <BlackGoldLoginModal
      ref="loginModal"
      :visible="loginModalOpen"
      @close="onLoginModalClose"
      @signup="onSignupFromLogin"
      @submit="onLoginSubmit"
    />

    <BlackGoldSignupModal
      ref="signupModal"
      :visible="signupModalOpen"
      @close="onSignupModalClose"
      @login="onSignupGoLogin"
      @telegram="onTelegram"
      @submit="onSignupSubmit"
    />
    <BlackGoldLangModal
      :visible="langModalOpen"
      @close="onLangModalClose"
      @change="onLangChange"
    />

    <BlackGoldNoticeDetailModal
      :visible="noticeDetailOpen"
      :notice-idx="noticeDetailIdx"
      @close="closeNoticeDetail"
    />

    <BlackGoldPopupNoticeLayer
      ref="popupNotices"
      @visibility-change="onPopupNoticeVisibility"
    />

    <BlackGoldQuestionDetailModal
      :visible="questionDetailOpen"
      :question-idx="questionDetailIdx"
      @close="closeQuestionDetail"
    />

    <BlackGoldSlipDetailModal
      :visible="slipDetailOpen"
      :slip-idx="slipDetailIdx"
      @close="closeSlipDetail"
      @read="onSlipRead"
    />

    <BlackGoldGamePreviewModal
      :visible="slotPreviewOpen"
      :game="slotPreviewGame"
      :category-label="gamePreviewCategoryLabel"
      :show-demo="gamePreviewShowDemo"
      @close="closeSlotPreview"
      @play-real="onSlotPlayReal"
      @play-demo="onSlotPlayDemo"
    />

    <BlackGoldAlertModal
      :visible="slipUnreadModalOpen"
      :message="$t('slip.unreadCheckMessage')"
      :confirm-label="$t('slip.unreadCheckConfirm')"
      :show-cancel="true"
      @close="onSlipUnreadModalClose"
      @confirm="onSlipUnreadModalConfirm"
    />

  </div>
</template>

<script>
import BlackGoldHeader from '@/components/black-gold/BlackGoldHeader.vue'
import BlackGoldSidebar from '@/components/black-gold/BlackGoldSidebar.vue'
import BlackGoldHero from '@/components/black-gold/BlackGoldHero.vue'
import BlackGoldGames from '@/components/black-gold/BlackGoldGames.vue'
import BlackGoldProviders from '@/components/black-gold/BlackGoldProviders.vue'
import BlackGoldSlotGames from '@/components/black-gold/BlackGoldSlotGames.vue'
import BlackGoldGamePreviewModal from '@/components/black-gold/BlackGoldGamePreviewModal.vue'
import BlackGoldSportsFrame from '@/components/black-gold/BlackGoldSportsFrame.vue'
import BlackGoldMyPage from '@/components/black-gold/BlackGoldMyPage.vue'
import BlackGoldNoticePage from '@/components/black-gold/BlackGoldNoticePage.vue'
import BlackGoldEventPage from '@/components/black-gold/BlackGoldEventPage.vue'
import BlackGoldSupportPage from '@/components/black-gold/BlackGoldSupportPage.vue'
import BlackGoldSlipPage from '@/components/black-gold/BlackGoldSlipPage.vue'
import BlackGoldBettingPage from '@/components/black-gold/BlackGoldBettingPage.vue'
import BlackGoldDepositPage from '@/components/black-gold/BlackGoldDepositPage.vue'
import BlackGoldWithdrawPage from '@/components/black-gold/BlackGoldWithdrawPage.vue'
import BlackGoldMobileNav from '@/components/black-gold/BlackGoldMobileNav.vue'
import BlackGoldPageLoader from '@/components/black-gold/BlackGoldPageLoader.vue'
import BlackGoldLoginModal from '@/components/black-gold/BlackGoldLoginModal.vue'
import BlackGoldSignupModal from '@/components/black-gold/BlackGoldSignupModal.vue'
import BlackGoldLangModal from '@/components/black-gold/BlackGoldLangModal.vue'
import BlackGoldNoticeDetailModal from '@/components/black-gold/BlackGoldNoticeDetailModal.vue'
import BlackGoldPopupNoticeLayer from '@/components/black-gold/BlackGoldPopupNoticeLayer.vue'
import BlackGoldQuestionDetailModal from '@/components/black-gold/BlackGoldQuestionDetailModal.vue'
import BlackGoldSlipDetailModal from '@/components/black-gold/BlackGoldSlipDetailModal.vue'
import BlackGoldAlertModal from '@/components/black-gold/BlackGoldAlertModal.vue'
import { AuthService, GameService, SystemService } from '@/services'
import { resolveRuntimeSlipUnreadCount } from '@/utils/slip'
import { clearSession, getLoginId, handleLoginResponse, isLoggedIn as checkLoggedIn, SessionBus } from '@/utils/auth'
import {
  filterProvidersByCategory,
  excludeExclusiveTabProviders,
  findProvidersByName,
  getProviderIdx,
  resolveProviderName,
  resolveProviderDisplayName,
  resolveProviderFilename,
  resolveProviderGameSymbol,
  normalizeProviders,
  filterGameNavItems,
  filterVisibleProviders,
  GAME_CATEGORY,
  SPORTS_PROVIDER_NAME,
  MINI_GAME_PROVIDER_NAME
} from '@/utils/provider'
import {
  SLOT_CATEGORY,
  MINI_GAME_CATEGORY,
  FISHING_CATEGORY,
  normalizeFishingGames,
  resolveFishingVendorId
} from '@/utils/game'
import { getSignupErrorMessage, validateSignupForm } from '@/utils/signup'
import { isLiveFeatureEnabled } from '@/utils/liveFeature'
import { setBodyScrollLocked, resetBodyScrollLock } from '@/utils/scrollLock'
import { showToast } from '@/utils/toast'
import {
  SIDE_MENU_ITEMS,
  isFishingUsageEnabled,
  MAIN_NAV_ITEMS,
  MOBILE_NAV_ITEMS
} from '@/data/blackGoldSite'
import {
  filterSideMenuByWallet,
  isSiteWalletEnabled
} from '@/utils/paymentMethod'
import { translateNavItems } from '@/utils/locale'
import { resolveHeroSlides } from '@/utils/heroSlides'
import { getSiteMaintenanceMessage, isSiteUnderMaintenance } from '@/utils/siteStatus'
import BlackGoldMaintenanceOverlay from '@/components/black-gold/BlackGoldMaintenanceOverlay.vue'

const BOOT_MIN_MS = 900
const BALANCE_POLL_MS = 5000

export default {
  name: 'Home',
  components: {
    BlackGoldHeader,
    BlackGoldSidebar,
    BlackGoldHero,
    BlackGoldGames,
    BlackGoldProviders,
    BlackGoldSlotGames,
    BlackGoldGamePreviewModal,
    BlackGoldSportsFrame,
    BlackGoldMyPage,
    BlackGoldNoticePage,
    BlackGoldEventPage,
    BlackGoldSupportPage,
    BlackGoldSlipPage,
    BlackGoldBettingPage,
    BlackGoldDepositPage,
    BlackGoldWithdrawPage,
    BlackGoldMobileNav,
    BlackGoldPageLoader,
    BlackGoldLoginModal,
    BlackGoldSignupModal,
    BlackGoldLangModal,
    BlackGoldNoticeDetailModal,
    BlackGoldPopupNoticeLayer,
    BlackGoldQuestionDetailModal,
    BlackGoldSlipDetailModal,
    BlackGoldAlertModal,
    BlackGoldMaintenanceOverlay
  },
  data () {
    return {
      pageBooting: true,
      pageReady: false,
      loggedIn: checkLoggedIn(),
      loginModalOpen: false,
      signupModalOpen: false,
      langModalOpen: false,
      balanceLoading: false,
      balancePollTimer: null,
      member: {
        loginid: getLoginId(),
        balance: null
      },
      hotGames: [],
      hotGamesLoading: false,
      hotGamesError: '',
      providers: [],
      providersLoading: false,
      providersError: '',
      selectedSlotProvider: null,
      slotGames: [],
      slotGamesLoading: false,
      slotGamesError: '',
      telegramId: window.localStorage.getItem('TEL_ID') || '',
      activeNav: 0,
      mainView: 'home',
      activeSide: 'mypage',
      menuOpen: false,
      pendingPlayGame: null,
      noticeDetailOpen: false,
      noticeDetailIdx: null,
      questionDetailOpen: false,
      questionDetailIdx: null,
      slipDetailOpen: false,
      slipDetailIdx: null,
      slipUnreadCount: 0,
      slipUnreadAlertShown: false,
      slipUnreadModalOpen: false,
      popupNoticeOpen: false,
      slotPreviewOpen: false,
      slotPreviewGame: null,
      gameLaunchLoading: false,
      pendingLiveProvider: null,
      pendingSportsOpen: false,
      sportsFrameUrl: '',
      sportsFrameLoading: false,
      sportsFrameError: '',
      minigameGames: [],
      minigameGamesLoading: false,
      minigameGamesError: '',
      minigameProviderName: '',
      minigameListCategory: MINI_GAME_CATEGORY,
      fishingGames: [],
      fishingGamesLoading: false,
      fishingGamesError: '',
      fishingUsageEnabled: false,
      gamePreviewCategory: SLOT_CATEGORY,
      gamePreviewShowDemo: true
    }
  },
  computed: {
    mainNavItems () {
      const locale = this.$i18n.locale
      void locale
      const items = filterGameNavItems(MAIN_NAV_ITEMS, {
        providers: this.providers,
        fishingUsageEnabled: this.fishingUsageEnabled
      })
      return translateNavItems(items, key => this.$t(key))
    },
    mobileNavItems () {
      const locale = this.$i18n.locale
      void locale
      const items = filterGameNavItems(MOBILE_NAV_ITEMS, {
        providers: this.providers,
        fishingUsageEnabled: this.fishingUsageEnabled
      })
      return translateNavItems(items, key => this.$t(key))
    },
    mobileNavActive () {
      const item = this.mainNavItems[this.activeNav]
      return (item && item.key) || 'home'
    },
    gamePreviewCategoryLabel () {
      if (this.gamePreviewCategory === MINI_GAME_CATEGORY) return 'MINI'
      if (this.gamePreviewCategory === FISHING_CATEGORY) return 'FISHING'
      return 'SLOT'
    },
    liveFeatureEnabled () {
      return isLiveFeatureEnabled()
    },
    liveProviders () {
      if (!this.liveFeatureEnabled) return []
      return excludeExclusiveTabProviders(
        filterProvidersByCategory(this.providers, GAME_CATEGORY.LIVE)
      )
    },
    slotProviders () {
      return filterProvidersByCategory(this.providers, GAME_CATEGORY.SLOT)
    },
    popularProviders () {
      return filterVisibleProviders(this.providers, {
        liveEnabled: this.liveFeatureEnabled
      })
    },
    sportsProviders () {
      return findProvidersByName(this.providers, SPORTS_PROVIDER_NAME)
    },
    spribeProvider () {
      const list = findProvidersByName(this.providers, MINI_GAME_PROVIDER_NAME)
      return list.length ? list[0] : null
    },
    sideMenuBadges () {
      return {
        slip: this.slipUnreadCount
      }
    },
    walletEnabled () {
      return isSiteWalletEnabled(this.$store.state.siteInfo)
    },
    sideMenuItems () {
      const locale = this.$i18n.locale
      void locale
      const translated = translateNavItems(SIDE_MENU_ITEMS, key => this.$t(key))
      return filterSideMenuByWallet(translated, this.walletEnabled)
    },
    siteMaintenanceActive () {
      return isSiteUnderMaintenance(this.$store.state.siteInfo)
    },
    siteMaintenanceMessage () {
      return getSiteMaintenanceMessage(this.$store.state.siteInfo)
    }
  },
  watch: {
    menuOpen (open) {
      this.syncBodyScrollLock()
    },
    pageBooting (booting) {
      this.syncBodyScrollLock()
    },
    loginModalOpen () {
      this.syncBodyScrollLock()
    },
    signupModalOpen () {
      this.syncBodyScrollLock()
    },
    gameLaunchLoading () {
      this.syncBodyScrollLock()
    },
    slotPreviewOpen () {
      this.syncBodyScrollLock()
    },
    slipUnreadModalOpen () {
      this.syncBodyScrollLock()
    },
    siteMaintenanceActive (active) {
      if (active) {
        this.enterMaintenanceMode()
      }
      this.syncBodyScrollLock()
    },
    mainNavItems () {
      this.leaveUnavailableGameNavViewIfNeeded()
      this.syncActiveNavIndex()
    },
    walletEnabled () {
      this.leaveWalletViewIfNeeded()
    },
    providers () {
      if (this.mainView === 'sports' && this.loggedIn) {
        if (!this.sportsFrameLoading && !this.sportsFrameUrl && this.sportsProviders.length) {
          this.loadSportsFrame()
        }
      }
      if (this.mainView === 'minigames') {
        if (!this.minigameGamesLoading && !this.minigameGames.length && !this.minigameGamesError) {
          if (this.spribeProvider) {
            this.loadMinigameGames()
          }
        }
      }
      if (this.mainView === 'fishing') {
        if (!this.fishingGamesLoading && !this.fishingGames.length && !this.fishingGamesError) {
          this.loadFishingGames()
        }
      }
    }
  },
  mounted () {
    SessionBus.$on('session:logout', this.onForceLogout)
    this.initPage()
  },
  beforeDestroy () {
    SessionBus.$off('session:logout', this.onForceLogout)
    this.stopBalancePoll()
    resetBodyScrollLock()
  },
  methods: {
    syncBodyScrollLock () {
      const locked = this.pageBooting ||
        this.siteMaintenanceActive ||
        this.gameLaunchLoading ||
        this.menuOpen ||
        this.loginModalOpen ||
        this.signupModalOpen ||
        this.popupNoticeOpen ||
        this.slotPreviewOpen ||
        this.slipUnreadModalOpen
      setBodyScrollLocked(locked)
    },
    async initPage () {
      this.pageBooting = true
      this.pageReady = false

      try {
        await Promise.all([
          this.delay(BOOT_MIN_MS),
          this.loadHotGames(),
          this.loadSiteInfo(),
          this.loadBannerList()
        ])
      } finally {
        this.pageBooting = false
      }

      if (this.siteMaintenanceActive) {
        this.enterMaintenanceMode()
        return
      }

      this.$nextTick(() => {
        requestAnimationFrame(() => {
          this.pageReady = true
          this.preloadHeroSlides()
          this.$nextTick(() => {
            this.showPopupNotices()
          })
        })
      })
      if (this.loggedIn) {
        await this.loadMemberRuntime()
        this.startBalancePoll()
      }
    },
    enterMaintenanceMode () {
      this.pageReady = false
      this.menuOpen = false
      this.stopBalancePoll()
      this.loginModalOpen = false
      this.signupModalOpen = false
      this.langModalOpen = false
      this.noticeDetailOpen = false
      this.questionDetailOpen = false
      this.slipDetailOpen = false
      this.slipUnreadModalOpen = false
      this.slotPreviewOpen = false
      this.gameLaunchLoading = false
      this.popupNoticeOpen = false
      const popupLayer = this.$refs.popupNotices
      if (popupLayer && popupLayer.closeAll) popupLayer.closeAll()
      this.syncBodyScrollLock()
    },
    startBalancePoll () {
      this.stopBalancePoll()
      if (!this.loggedIn) return
      this.balancePollTimer = window.setInterval(() => {
        if (this.loggedIn) {
          this.loadMemberRuntime({ silent: true })
        } else {
          this.stopBalancePoll()
        }
      }, BALANCE_POLL_MS)
    },
    stopBalancePoll () {
      if (this.balancePollTimer) {
        clearInterval(this.balancePollTimer)
        this.balancePollTimer = null
      }
    },
    async loadMemberRuntime (options = {}) {
      const silent = !!options.silent
      if (!checkLoggedIn()) {
        this.loggedIn = false
        this.member.loginid = ''
        this.member.balance = null
        return
      }
      if (!silent) this.balanceLoading = true
      try {
        const res = await GameService.getBalanceApi()
        const body = res && res.data ? res.data : null
        if (body && body.code === 0 && body.data) {
          this.member.loginid = getLoginId()
          this.member.balance = body.data.balance
          if (body.data.tel_id) {
            this.telegramId = body.data.tel_id
            window.localStorage.setItem('TEL_ID', body.data.tel_id)
          }
          this.applySlipUnreadCount(
            resolveRuntimeSlipUnreadCount(body.data),
            { notify: false }
          )
        }
      } catch (err) {
        // 인증/네트워크 오류는 axios 인터셉터에서 로그아웃 처리
      } finally {
        if (!silent) this.balanceLoading = false
      }
    },
    onForceLogout (payload) {
      const message = payload && payload.message
      this.handleSessionExpired(message, { toast: false })
    },
    handleSessionExpired (message, options = {}) {
      clearSession()
      this.loggedIn = false
      this.stopBalancePoll()
      this.member.loginid = ''
      this.member.balance = null
      this.closeNoticeDetail()
      this.closeQuestionDetail()
      this.closeSlipDetail()
      const popupLayer = this.$refs.popupNotices
      if (popupLayer && popupLayer.closeAll) popupLayer.closeAll()
      if (this.mainView === 'mypage' || this.mainView === 'notice' || this.mainView === 'event' || this.mainView === 'support' || this.mainView === 'slip' || this.mainView === 'betting' || this.mainView === 'deposit' || this.mainView === 'withdraw') {
        this.mainView = 'home'
        this.setActiveNavKey('home')
      }
      this.slipUnreadCount = 0
      this.slipUnreadAlertShown = false
      if (options.toast !== false && message) {
        this.showToast(message)
      }
    },
    delay (ms) {
      return new Promise(resolve => window.setTimeout(resolve, ms))
    },
    preloadAsset (url) {
      return new Promise(resolve => {
        const img = new Image()
        img.onload = img.onerror = () => resolve()
        img.src = url
      })
    },
    preloadHeroSlides () {
      const slides = resolveHeroSlides(this.$store.state.bannerList, this.$i18n.locale)
      return Promise.all(slides.map(url => this.preloadAsset(url)))
    },
    async loadBannerList () {
      try {
        const res = await SystemService.getBannerList()
        const body = res && res.data ? res.data : null
        if (body && body.code === 0 && Array.isArray(body.data)) {
          this.$store.commit('SET_BANNER_LIST', body.data)
        } else {
          this.$store.commit('SET_BANNER_LIST', [])
        }
      } catch (err) {
        this.$store.commit('SET_BANNER_LIST', [])
      }
    },
    async loadSiteInfo () {
      this.providersLoading = true
      this.providersError = ''
      try {
        const res = await SystemService.getSiteSettingInfo()
        const body = res && res.data ? res.data : null
        if (body && body.code === 0 && body.data) {
          const offProviders = body.data.off_providers
          this.providers = normalizeProviders(
            Array.isArray(offProviders) ? offProviders : []
          )
          this.fishingUsageEnabled = isFishingUsageEnabled(body.data.fishing_usage)
          this.$store.commit('SET_SITE_INFO', body.data)
          if (isSiteUnderMaintenance(body.data)) {
            this.enterMaintenanceMode()
            return
          }
          this.$nextTick(() => {
            this.leaveUnavailableGameNavViewIfNeeded()
            this.leaveWalletViewIfNeeded()
            this.syncActiveNavIndex()
          })
          if (body.data.tel_id) {
            this.telegramId = body.data.tel_id
            window.localStorage.setItem('TEL_ID', body.data.tel_id)
          }
        } else {
          this.providers = []
          this.providersError = (body && body.message) ? body.message : this.$t('common.siteLoadError')
        }
      } catch (err) {
        this.providers = []
        this.providersError = this.$t('common.siteLoadError')
      } finally {
        this.providersLoading = false
      }
    },
    async loadHotGames () {
      this.hotGamesLoading = true
      this.hotGamesError = ''
      try {
        const res = await GameService.getHotGameList()
        const body = res && res.data ? res.data : null
        if (body && body.code === 0 && Array.isArray(body.data)) {
          this.hotGames = body.data
        } else {
          this.hotGames = []
          this.hotGamesError = (body && body.message) ? body.message : this.$t('games.loadError')
        }
      } catch (err) {
        this.hotGames = []
        this.hotGamesError = this.$t('games.loadError')
      } finally {
        this.hotGamesLoading = false
      }
    },
    showToast (message) {
      showToast(message)
    },
    scrollTo (id) {
      const el = document.getElementById(id)
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' })
    },
    toggleMobileMenu () {
      this.menuOpen = !this.menuOpen
    },
    closeMobileMenu () {
      this.menuOpen = false
    },
    hasUnreadSlip () {
      return this.loggedIn && this.slipUnreadCount > 0
    },
    requireSlipRead () {
      if (!this.hasUnreadSlip()) return true
      this.slipUnreadModalOpen = true
      return false
    },
    onSlipUnreadModalClose () {
      this.slipUnreadModalOpen = false
    },
    onSlipUnreadModalConfirm () {
      this.slipUnreadModalOpen = false
      this.openSlipPage()
    },
    goHome () {
      if (!this.requireSlipRead()) return
      this.setActiveNavKey('home')
      this.mainView = 'home'
      this.clearSlotProvider()
      this.clearSportsFrame()
      this.clearMinigameGames()
      this.clearFishingGames()
      this.menuOpen = false
      this.scrollTo('section-home')
    },
    onTelegram () {
      let telId = (this.telegramId || window.localStorage.getItem('TEL_ID') || '').trim()
      if (!telId) {
        this.showToast(this.$t('common.telegramMissing'))
        return
      }
      if (telId.startsWith('@')) {
        telId = telId.substring(1)
      }
      window.open('https://t.me/' + telId, '_blank', 'noopener,noreferrer')
    },
    onMobileNav (key) {
      const index = this.mainNavItems.findIndex(item => item.key === key)
      if (index >= 0) {
        this.onTopNav(index)
      }
    },
    setActiveNavKey (key) {
      const index = this.mainNavItems.findIndex(item => item.key === key)
      this.activeNav = index >= 0 ? index : 0
    },
    syncActiveNavIndex () {
      const keys = this.mainNavItems.map(item => item.key)
      const viewKey = {
        home: 'home',
        live: 'live',
        slots: 'slots',
        sports: 'sports',
        minigames: 'minigames',
        fishing: 'fishing'
      }[this.mainView]
      if (viewKey && keys.includes(viewKey)) {
        this.setActiveNavKey(viewKey)
        return
      }
      if (this.activeNav >= this.mainNavItems.length) {
        this.activeNav = 0
      }
    },
    leaveWalletViewIfNeeded () {
      if (this.walletEnabled) return
      if (this.mainView !== 'deposit' && this.mainView !== 'withdraw') return
      this.mainView = 'mypage'
      this.activeSide = 'mypage'
    },
    leaveUnavailableGameNavViewIfNeeded () {
      const viewKey = {
        live: 'live',
        slots: 'slots',
        sports: 'sports',
        minigames: 'minigames',
        fishing: 'fishing'
      }[this.mainView]
      if (!viewKey) return
      const available = this.mainNavItems.some(item => item.key === viewKey)
      if (available) return
      if (viewKey === 'slots') this.clearSlotProvider()
      if (viewKey === 'sports') this.clearSportsFrame()
      if (viewKey === 'minigames') this.clearMinigameGames()
      if (viewKey === 'fishing') this.clearFishingGames()
      this.setActiveNavKey('home')
      this.mainView = 'home'
    },
    onTopNav (index) {
      const item = this.mainNavItems[index]
      if (!item) return
      this.onMainNavKey(item.key)
    },
    onMainNavKey (key) {
      if (!this.requireSlipRead()) return
      this.menuOpen = false
      if (key === 'home') {
        this.goHome()
        return
      }
      if (key === 'live') {
        if (!this.liveFeatureEnabled) return
        this.setActiveNavKey('live')
        this.clearSportsFrame()
        this.clearMinigameGames()
        this.clearFishingGames()
        this.mainView = 'live'
        this.$nextTick(() => {
          this.scrollTo('section-live')
        })
        return
      }
      if (key === 'slots') {
        this.setActiveNavKey('slots')
        this.clearSportsFrame()
        this.clearMinigameGames()
        this.clearFishingGames()
        this.clearSlotProvider()
        this.mainView = 'slots'
        this.$nextTick(() => {
          this.scrollTo('section-slots')
        })
        return
      }
      if (key === 'sports') {
        if (!this.requireLogin()) {
          this.pendingSportsOpen = true
          return
        }
        this.openSportsView()
        return
      }
      if (key === 'minigames') {
        this.openMinigameView()
        return
      }
      if (key === 'fishing') {
        if (!this.mainNavItems.some(item => item.key === 'fishing')) return
        this.openFishingView()
      }
    },
    onSideNav (key) {
      this.closeMobileMenu()
      if (key !== 'slip' && !this.requireSlipRead()) return
      this.activeSide = key
      if (key === 'mypage') {
        this.openMyPage()
        return
      }
      if (key === 'deposit') {
        this.openDepositPage()
        return
      }
      if (key === 'withdraw') {
        this.openWithdrawPage()
        return
      }
      if (key === 'notice') {
        this.openNoticePage()
        return
      }
      if (key === 'event') {
        this.openEventPage()
        return
      }
      if (key === 'support') {
        this.openSupportPage()
        return
      }
      if (key === 'slip') {
        this.openSlipPage()
        return
      }
      if (key === 'betting') {
        this.openBettingPage()
      }
    },
    openMyPage () {
      if (!this.requireSlipRead()) return
      if (!this.requireLogin()) return
      this.mainView = 'mypage'
      this.activeSide = 'mypage'
      this.$nextTick(() => {
        this.scrollTo('section-mypage')
        const page = this.$refs.myPage
        if (page && page.reload) {
          page.reload()
        }
      })
    },
    openNoticePage () {
      if (!this.requireSlipRead()) return
      if (!this.requireLogin()) return
      this.mainView = 'notice'
      this.activeSide = 'notice'
      this.$nextTick(() => {
        this.scrollTo('section-notice')
        const page = this.$refs.noticePage
        if (page && page.reload) {
          page.reload()
        }
      })
    },
    openEventPage () {
      if (!this.requireSlipRead()) return
      if (!this.requireLogin()) return
      this.mainView = 'event'
      this.activeSide = 'event'
      this.$nextTick(() => {
        this.scrollTo('section-event')
        const page = this.$refs.eventPage
        if (page && page.reload) {
          page.reload()
        }
      })
    },
    openSupportPage () {
      if (!this.requireSlipRead()) return
      if (!this.requireLogin()) return
      this.mainView = 'support'
      this.activeSide = 'support'
      this.$nextTick(() => {
        this.scrollTo('section-support')
        const page = this.$refs.supportPage
        if (page && page.reload) {
          page.reload()
        }
      })
    },
    openSlipPage () {
      if (!this.requireLogin()) return
      this.mainView = 'slip'
      this.activeSide = 'slip'
      this.$nextTick(() => {
        this.scrollTo('section-slip')
        const page = this.$refs.slipPage
        if (page && page.reload) {
          page.reload()
        }
      })
    },
    openBettingPage () {
      if (!this.requireSlipRead()) return
      if (!this.requireLogin()) return
      this.mainView = 'betting'
      this.activeSide = 'betting'
      this.$nextTick(() => {
        this.scrollTo('section-betting')
        const page = this.$refs.bettingPage
        if (page && page.reload) {
          page.reload()
        }
      })
    },
    openNoticeDetail (idx) {
      if (idx == null || idx === '') return
      this.noticeDetailIdx = idx
      this.noticeDetailOpen = true
    },
    closeNoticeDetail () {
      this.noticeDetailOpen = false
      this.noticeDetailIdx = null
    },
    openQuestionDetail (idx) {
      if (idx == null || idx === '') return
      this.questionDetailIdx = idx
      this.questionDetailOpen = true
    },
    closeQuestionDetail () {
      this.questionDetailOpen = false
      this.questionDetailIdx = null
    },
    openSlipDetail (idx) {
      if (idx == null || idx === '') return
      this.slipDetailIdx = idx
      this.slipDetailOpen = true
    },
    closeSlipDetail () {
      this.slipDetailOpen = false
      this.slipDetailIdx = null
      this.loadMemberRuntime({ silent: true })
      const page = this.$refs.slipPage
      if (page && page.reload) {
        page.reload()
      }
    },
    onSlipRead () {
      this.loadMemberRuntime({ silent: true })
      const page = this.$refs.slipPage
      if (page && page.reload) {
        page.reload()
      }
    },
    applySlipUnreadCount (count, options = {}) {
      const next = Math.max(0, Number(count) || 0)
      const prev = this.slipUnreadCount
      this.slipUnreadCount = next

      if (next === 0) {
        this.slipUnreadAlertShown = false
        return
      }

      const shouldNotify = options.notify !== false
      if (!shouldNotify) return

      if (!this.slipUnreadAlertShown || next > prev) {
        showToast(this.$t('slip.headerNotice'))
        this.slipUnreadAlertShown = true
      }
    },
    onPopupNoticeVisibility (open) {
      this.popupNoticeOpen = !!open
      this.syncBodyScrollLock()
    },
    showPopupNotices () {
      const layer = this.$refs.popupNotices
      if (layer && layer.loadAndShow) {
        layer.loadAndShow()
      }
    },
    onWithdraw () {
      this.openWithdrawPage()
    },
    async onMyPageBalanceUpdated () {
      await this.loadMemberRuntime({ silent: true })
    },
    onInquiry () {
      if (!this.requireSlipRead()) return
      if (!this.requireLogin()) return
      this.openSupportPage()
    },
    onBettingHistory () {
      this.openBettingPage()
    },
    onLogin () {
      this.openLoginModal()
    },
    onLoginModalClose () {
      this.loginModalOpen = false
      this.pendingPlayGame = null
      this.pendingLiveProvider = null
      this.pendingSportsOpen = false
    },
    openLoginModal (pendingGame) {
      if (pendingGame) {
        this.pendingPlayGame = pendingGame
      }
      this.loginModalOpen = true
    },
    requireLogin (pendingGame) {
      const authed = checkLoggedIn()
      if (!authed) {
        this.loggedIn = false
        this.openLoginModal(pendingGame)
        return false
      }
      if (!this.loggedIn) {
        this.loggedIn = true
      }
      return true
    },
    async onLogout () {
      try {
        await AuthService.logout()
      } catch (err) {
        // 세션 정리는 로컬에서 진행
      }
      clearSession()
      this.loggedIn = false
      this.stopBalancePoll()
      this.member.loginid = ''
      this.member.balance = null
      this.closeNoticeDetail()
      this.closeQuestionDetail()
      this.closeSlipDetail()
      const popupLayer = this.$refs.popupNotices
      if (popupLayer && popupLayer.closeAll) popupLayer.closeAll()
      if (this.mainView === 'mypage' || this.mainView === 'notice' || this.mainView === 'event' || this.mainView === 'support' || this.mainView === 'slip' || this.mainView === 'betting' || this.mainView === 'deposit' || this.mainView === 'withdraw' || this.mainView === 'sports' || this.mainView === 'minigames' || this.mainView === 'fishing') {
        this.mainView = 'home'
        this.setActiveNavKey('home')
      }
      this.slipUnreadCount = 0
      this.slipUnreadAlertShown = false
      this.pendingLiveProvider = null
      this.pendingSportsOpen = false
      this.clearSportsFrame()
      this.clearMinigameGames()
      this.clearFishingGames()
      this.showToast(this.$t('auth.logoutSuccess'))
    },
    onSignupFromLogin () {
      this.onLoginModalClose()
      this.onSignup()
    },
    async onLoginSubmit (payload) {
      const modal = this.$refs.loginModal
      try {
        const res = await AuthService.login(payload)
        const body = res && res.data ? res.data : null
        const result = handleLoginResponse(body)
        if (!result.ok) {
          if (modal) modal.setError(result.message)
          return
        }
        if (modal) modal.finishSuccess()
        this.loginModalOpen = false
        this.loggedIn = true
        this.$store.commit('SET_LOGGED_IN', true)
        this.member.loginid = (result.data && result.data.loginid) || payload.loginid || getLoginId()
        await this.loadMemberRuntime()
        this.startBalancePoll()
        await this.reloadActiveGameLists()
        const pendingGame = this.pendingPlayGame
        const pendingLive = this.pendingLiveProvider
        const pendingSports = this.pendingSportsOpen
        this.pendingPlayGame = null
        this.pendingLiveProvider = null
        this.pendingSportsOpen = false
        if (!this.requireSlipRead()) {
          return
        }
        if (pendingGame) {
          const pendingCategory = pendingGame.category != null
            ? Number(pendingGame.category)
            : this.gamePreviewCategory
          if (pendingCategory === FISHING_CATEGORY) {
            await this.onPlayFishingGame(pendingGame)
          } else if (pendingCategory === MINI_GAME_CATEGORY) {
            await this.onPlayMinigameGame(pendingGame)
          } else {
            await this.onPlaySlotGame(pendingGame)
          }
        } else if (pendingLive) {
          await this.launchLiveProvider(pendingLive)
        } else if (pendingSports) {
          this.openSportsView()
        }
        if (this.mainView === 'mypage') {
          const page = this.$refs.myPage
          if (page && page.reload) {
            await page.reload()
          }
        }
        this.showToast(this.$t('auth.loginSuccess'))
        this.$nextTick(() => {
          this.showPopupNotices()
        })
      } catch (err) {
        if (modal) modal.setError(this.$t('auth.loginFail'))
      }
    },
    onSignup () {
      if (this.loggedIn) return
      this.loginModalOpen = false
      this.signupModalOpen = true
    },
    onSignupModalClose () {
      this.signupModalOpen = false
    },
    onSignupGoLogin () {
      this.signupModalOpen = false
      this.openLoginModal()
    },
    async onSignupSubmit (form) {
      const modal = this.$refs.signupModal
      const validationError = validateSignupForm(form)
      if (validationError) {
        if (modal) modal.setError(validationError)
        else this.showToast(validationError)
        return
      }

      try {
        const res = await AuthService.signup({
          loginid: form.loginid,
          loginpwd: form.loginpwd,
          name: form.name,
          tel: form.verifiedPhone || '',
          bank_name: '',
          bank_account: '',
          bank_master: '',
          pcode: form.pcode
        })
        const body = res && res.data ? res.data : null
        if (!body || body.code !== 0) {
          const message = getSignupErrorMessage(body && body.code, body && body.message)
          if (modal) modal.setError(message)
          return
        }

        const loginRes = await AuthService.login({
          loginid: form.loginid,
          loginpwd: form.loginpwd
        })
        const loginBody = loginRes && loginRes.data ? loginRes.data : null
        const result = handleLoginResponse(loginBody)
        if (!result.ok) {
          if (modal) modal.finishSuccess()
          this.signupModalOpen = false
          this.showToast(this.$t('auth.signupSuccessLogin'))
          this.openLoginModal()
          return
        }

        if (modal) modal.finishSuccess()
        this.signupModalOpen = false
        this.loggedIn = true
        this.$store.commit('SET_LOGGED_IN', true)
        this.member.loginid = (result.data && result.data.loginid) || form.loginid || getLoginId()
        await this.loadMemberRuntime()
        this.startBalancePoll()
        await this.reloadActiveGameLists()
        this.showToast(this.$t('auth.signupSuccess'))
        this.$nextTick(() => {
          this.showPopupNotices()
        })
      } catch (err) {
        if (modal) modal.setError(this.$t('auth.signupFail'))
      }
    },
    onLang () {
      if (!this.requireSlipRead()) return
      this.langModalOpen = true
    },
    onLangModalClose () {
      this.langModalOpen = false
    },
    async onLangChange () {
      this.langModalOpen = false
      this.showToast(this.$t('lang.changed'))
      if (typeof this.reloadActiveGameLists === 'function') {
        await this.reloadActiveGameLists()
      }
    },
    openDepositPage () {
      if (!this.walletEnabled) return
      if (!this.requireSlipRead()) return
      if (!this.requireLogin()) return
      this.mainView = 'deposit'
      this.activeSide = 'deposit'
      this.$nextTick(() => {
        this.scrollTo('section-deposit')
        const page = this.$refs.depositPage
        if (page && page.reload) {
          page.reload()
        }
      })
    },
    openWithdrawPage () {
      if (!this.walletEnabled) return
      if (!this.requireSlipRead()) return
      if (!this.requireLogin()) return
      this.mainView = 'withdraw'
      this.activeSide = 'withdraw'
      this.$nextTick(() => {
        this.scrollTo('section-withdraw')
        const page = this.$refs.withdrawPage
        if (page && page.reload) {
          page.reload()
        }
      })
    },
    onWithdrawSuccess () {
      this.loadMemberRuntime({ silent: true })
    },
    onDeposit () {
      this.openDepositPage()
    },
    onDepositSuccess () {
      this.loadMemberRuntime({ silent: true })
    },
    clearSlotProvider () {
      if (!this.requireSlipRead()) return
      this.closeSlotPreview()
      this.selectedSlotProvider = null
      this.slotGames = []
      this.slotGamesLoading = false
      this.slotGamesError = ''
    },
    openSlotPreview (game) {
      if (!this.requireSlipRead()) return
      if (!game) return
      this.gamePreviewCategory = SLOT_CATEGORY
      this.gamePreviewShowDemo = true
      this.slotPreviewGame = game
      this.slotPreviewOpen = true
    },
    openMinigamePreview (game) {
      if (!this.requireSlipRead()) return
      if (!game) return
      this.gamePreviewCategory = MINI_GAME_CATEGORY
      this.gamePreviewShowDemo = false
      this.slotPreviewGame = game
      this.slotPreviewOpen = true
    },
    openFishingPreview (game) {
      if (!this.requireSlipRead()) return
      if (!game) return
      this.gamePreviewCategory = FISHING_CATEGORY
      this.gamePreviewShowDemo = false
      this.slotPreviewGame = game
      this.slotPreviewOpen = true
    },
    closeSlotPreview () {
      this.slotPreviewOpen = false
      this.slotPreviewGame = null
      this.$nextTick(() => this.syncBodyScrollLock())
    },
    onSlotPlayReal () {
      if (!this.requireSlipRead()) return
      const game = this.slotPreviewGame
      const category = this.gamePreviewCategory
      this.closeSlotPreview()
      if (category === MINI_GAME_CATEGORY) {
        this.onPlayMinigameGame(game)
        return
      }
      if (category === FISHING_CATEGORY) {
        this.onPlayFishingGame(game)
        return
      }
      this.onPlaySlotGame(game)
    },
    async onSlotPlayDemo () {
      if (!this.requireSlipRead()) return
      const game = this.slotPreviewGame
      if (!game) return
      this.closeSlotPreview()
      await this.launchSlotGameWindow({
        mode: 'demo',
        provider: Number(game.provider),
        code: game.code
      })
    },
    openGameWindow (url, windowName) {
      if (!url) return false
      window.open(url, windowName, 'width=1350,height=900,status=no,scrollbars=yes,resizable=yes')
      return true
    },
    async launchGameWindow ({ mode, provider, code, category, vendorId = -1 }) {
      if (this.gameLaunchLoading) return
      this.gameLaunchLoading = true
      this.$nextTick(() => this.syncBodyScrollLock())
      try {
        const res = mode === 'demo'
          ? await GameService.startDemoGame(provider, code, 'fun')
          : await GameService.startNewGame(provider, code, 'PC', category, 'real', vendorId)
        const body = res && res.data ? res.data : null
        if (body && body.code === 0 && body.data && body.data.url) {
          this.openGameWindow(body.data.url, mode === 'demo' ? 'game_demo' : 'game_main')
          return
        }
        const fallback = mode === 'demo' ? this.$t('games.demoError') : this.$t('games.launchError')
        this.showToast((body && body.message) || fallback)
      } catch (err) {
        this.showToast(mode === 'demo' ? this.$t('games.demoError') : this.$t('games.launchError'))
      } finally {
        this.gameLaunchLoading = false
        this.$nextTick(() => this.syncBodyScrollLock())
      }
    },
    async launchSlotGameWindow ({ mode, provider, code }) {
      await this.launchGameWindow({
        mode,
        provider,
        code,
        category: SLOT_CATEGORY
      })
    },
    getSlotProviderMeta (provider) {
      if (!provider) return null
      const idx = getProviderIdx(provider)
      if (idx == null) return null
      const filename = resolveProviderFilename(provider)
      return {
        idx,
        name: resolveProviderDisplayName(provider, filename)
      }
    },
    async onSelectSlotProvider (provider) {
      if (!this.requireSlipRead()) return
      if (!provider) return
      const meta = this.getSlotProviderMeta(provider)
      if (!meta) {
        this.showToast(this.$t('games.providerNotFound'))
        return
      }
      this.setActiveNavKey('slots')
      this.mainView = 'slots'
      this.selectedSlotProvider = meta
      await this.loadSlotGames(meta.idx)
      this.$nextTick(() => {
        this.scrollTo('section-slots-games')
      })
    },
    async reloadActiveGameLists () {
      if (this.selectedSlotProvider && this.selectedSlotProvider.idx != null) {
        await this.loadSlotGames(this.selectedSlotProvider.idx)
      }
      if (this.mainView === 'minigames' && this.spribeProvider) {
        await this.loadMinigameGames()
      }
    },
    async loadSlotGames (providerId) {
      this.slotGamesLoading = true
      this.slotGamesError = ''
      this.slotGames = []
      try {
        const res = await GameService.gameList(providerId, SLOT_CATEGORY)
        const body = res && res.data ? res.data : null
        if (body && body.code === 0 && Array.isArray(body.data)) {
          this.slotGames = body.data
        } else {
          this.slotGames = []
          this.slotGamesError = (body && body.message) ? body.message : this.$t('games.loadError')
        }
      } catch (err) {
        this.slotGames = []
        this.slotGamesError = this.$t('games.loadError')
      } finally {
        this.slotGamesLoading = false
      }
    },
    onSelectProvider (provider) {
      if (!this.requireSlipRead()) return
      if (!provider) return
      const category = Number(provider.category)
      if (category === GAME_CATEGORY.SLOT) {
        this.onSelectSlotProvider(provider)
        return
      }
      if (category === GAME_CATEGORY.LIVE) {
        if (!this.liveFeatureEnabled) return
        this.onSelectLiveProvider(provider)
      }
    },
    onSelectLiveProvider (provider) {
      if (!this.liveFeatureEnabled) return
      if (!this.requireSlipRead()) return
      if (!provider) return
      if (!this.requireLogin()) {
        this.pendingLiveProvider = provider
        return
      }
      this.launchLiveProvider(provider)
    },
    async launchLiveProvider (provider) {
      if (!this.requireSlipRead()) return
      const providerId = getProviderIdx(provider)
      if (providerId == null) {
        this.showToast(this.$t('games.providerNotFound'))
        return
      }
      await this.launchGameWindow({
        mode: 'real',
        provider: providerId,
        code: 'lobby',
        category: GAME_CATEGORY.LIVE
      })
    },
    openSportsView () {
      if (!this.requireSlipRead()) return
      this.clearMinigameGames()
      this.clearFishingGames()
      this.setActiveNavKey('sports')
      this.mainView = 'sports'
      this.menuOpen = false
      this.$nextTick(() => {
        this.scrollTo('section-sports')
        this.loadSportsFrame()
      })
    },
    clearMinigameGames () {
      this.closeSlotPreview()
      this.minigameGames = []
      this.minigameGamesLoading = false
      this.minigameGamesError = ''
      this.minigameProviderName = ''
      this.minigameListCategory = MINI_GAME_CATEGORY
    },
    openMinigameView () {
      if (!this.requireSlipRead()) return
      this.clearSportsFrame()
      this.clearSlotProvider()
      this.clearFishingGames()
      this.setActiveNavKey('minigames')
      this.mainView = 'minigames'
      this.menuOpen = false
      this.$nextTick(() => {
        this.scrollTo('section-minigames')
        this.loadMinigameGames()
      })
    },
    clearFishingGames () {
      this.closeSlotPreview()
      this.fishingGames = []
      this.fishingGamesLoading = false
      this.fishingGamesError = ''
    },
    openFishingView () {
      if (!this.requireSlipRead()) return
      if (!this.mainNavItems.some(item => item.key === 'fishing')) return
      this.clearSportsFrame()
      this.clearSlotProvider()
      this.clearMinigameGames()
      this.setActiveNavKey('fishing')
      this.mainView = 'fishing'
      this.menuOpen = false
      this.$nextTick(() => {
        this.scrollTo('section-fishing')
        this.loadFishingGames()
      })
    },
    async loadFishingGames () {
      if (!this.mainNavItems.some(item => item.key === 'fishing')) return
      this.fishingGamesLoading = true
      this.fishingGamesError = ''
      this.fishingGames = []
      try {
        const res = await GameService.getFishingList()
        const body = res && res.data ? res.data : null
        if (body && body.code === 0 && Array.isArray(body.data)) {
          this.fishingGames = normalizeFishingGames(body.data)
        } else {
          this.fishingGames = []
          this.fishingGamesError = (body && body.message) ? body.message : this.$t('games.loadError')
        }
      } catch (err) {
        this.fishingGames = []
        this.fishingGamesError = this.$t('games.loadError')
      } finally {
        this.fishingGamesLoading = false
      }
    },
    async onPlayFishingGame (game) {
      if (!this.requireSlipRead()) return
      if (!game) return
      if (!this.requireLogin(game)) return
      await this.launchGameWindow({
        mode: 'real',
        provider: Number(game.provider),
        code: game.code,
        category: FISHING_CATEGORY,
        vendorId: resolveFishingVendorId(game)
      })
    },
    async loadMinigameGames () {
      const provider = this.spribeProvider
      if (!provider) {
        this.minigameGames = []
        this.minigameGamesLoading = false
        this.minigameGamesError = this.$t('games.providerNotFoundMinigame', { provider: MINI_GAME_PROVIDER_NAME })
        this.minigameProviderName = ''
        return
      }
      const providerId = getProviderIdx(provider)
      if (providerId == null) {
        this.minigameGamesError = this.$t('games.providerNotFound')
        return
      }
      const filename = resolveProviderFilename(provider)
      this.minigameProviderName = resolveProviderDisplayName(provider, filename)
      const category = provider.category != null
        ? Number(provider.category)
        : MINI_GAME_CATEGORY
      this.minigameListCategory = category
      this.minigameGamesLoading = true
      this.minigameGamesError = ''
      this.minigameGames = []
      try {
        const res = await GameService.gameList(providerId, category)
        const body = res && res.data ? res.data : null
        if (body && body.code === 0 && Array.isArray(body.data)) {
          this.minigameGames = body.data
        } else {
          this.minigameGames = []
          this.minigameGamesError = (body && body.message) ? body.message : this.$t('games.loadError')
        }
      } catch (err) {
        this.minigameGames = []
        this.minigameGamesError = this.$t('games.loadError')
      } finally {
        this.minigameGamesLoading = false
      }
    },
    async onPlayMinigameGame (game) {
      if (!this.requireSlipRead()) return
      if (!game) return
      if (!this.requireLogin(game)) return
      const category = game.category != null
        ? Number(game.category)
        : this.minigameListCategory
      await this.launchGameWindow({
        mode: 'real',
        provider: Number(game.provider),
        code: game.code,
        category
      })
    },
    clearSportsFrame () {
      this.sportsFrameUrl = ''
      this.sportsFrameLoading = false
      this.sportsFrameError = ''
    },
    async loadSportsFrame () {
      if (!this.loggedIn) return
      const list = this.sportsProviders
      if (!list.length) {
        this.clearSportsFrame()
        this.sportsFrameError = this.$t('games.providerNotFoundSports', { provider: SPORTS_PROVIDER_NAME })
        return
      }
      await this.launchSportsInFrame(list[0])
    },
    async launchSportsInFrame (provider) {
      const providerId = getProviderIdx(provider)
      if (providerId == null) {
        this.sportsFrameError = this.$t('games.providerNotFound')
        return
      }
      this.sportsFrameLoading = true
      this.sportsFrameError = ''
      this.sportsFrameUrl = ''
      const category = provider.category != null
        ? Number(provider.category)
        : GAME_CATEGORY.SPORTS
      try {
        const res = await GameService.startNewGame(
          providerId,
          resolveProviderGameSymbol(provider),
          'PC',
          category,
          'real',
          -1
        )
        const body = res && res.data ? res.data : null
        if (body && body.code === 0 && body.data && body.data.url) {
          this.sportsFrameUrl = body.data.url
          return
        }
        this.sportsFrameError = (body && body.message) || this.$t('games.sportsLaunchError')
      } catch (err) {
        this.sportsFrameError = this.$t('games.sportsLaunchError')
      } finally {
        this.sportsFrameLoading = false
      }
    },
    async onPlaySlotGame (game) {
      if (!this.requireSlipRead()) return
      if (!game) return
      if (!this.requireLogin(game)) return
      await this.launchSlotGameWindow({
        mode: 'real',
        provider: Number(game.provider),
        code: game.code
      })
    }
  }
}
</script>
